import { useEffect, useMemo, useState } from 'react';
import { API_CONFIG, fetchAPI } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { initFacebookPixel, trackFacebookPageView } from '@/utils/facebookPixel';

type CreditOption = {
  id: string;
  label: string;
  credits: number;
  amount: number;
  priceLabel: string;
};

type PlanOption = {
  id: string;
  name: string;
  tier: string;
  priceLabel: string;
  monthlyPrice: number;
  includedCredits: number;
  descriptionLines: string[];
};

const CREDIT_OPTIONS: CreditOption[] = [
  { id: 'credits-100', label: '100 Credits', credits: 100, amount: 50, priceLabel: '$0.50/each' },
  { id: 'credits-500', label: '500 Credits', credits: 500, amount: 200, priceLabel: '$0.40/each' },
  { id: 'credits-10000', label: '10,000 Credits', credits: 10000, amount: 4000, priceLabel: '$0.40/each' },
];

const PLAN_OPTIONS: PlanOption[] = [
  {
    id: 'individual',
    name: 'Individual Starter',
    tier: 'Starter',
    priceLabel: '$50/month access + credits',
    monthlyPrice: 50,
    includedCredits: 500,
    descriptionLines: [
      '$0.50 per credit',
      'Minimum 500 credits/month ($250)',
      'Maximum 2,500 credits/month ($1,250)',
      'Minimum price charged is $300',
      'Maximum price charged is $1,300',
      'Individual account',
      'Email support',
      'Basic search features',
      '200+ data fields per search',
      'Multi-source intelligence',
    ],
  },
  {
    id: 'solopreneur',
    name: 'Solopreneur Pro',
    tier: 'Pro',
    priceLabel: '$2,997/month',
    monthlyPrice: 2997,
    includedCredits: 5000,
    descriptionLines: [
      '5,000 Credits Included',
      'First 5,000: Included ($0.60/credit equivalent)',
      '5,001-10,000: $0.20/credit',
      '10,001+: $0.10/credit',
      'Scheduled searches – 500 leads, 3x/week',
      'CRM with custom workflows',
      'Webinar & tradeshow automation',
      'Lead funnel management',
      'Automated CRM export',
      'Engagement Protocol available (add-on)',
      'Automated task creation',
      'Priority support',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    tier: 'Team',
    priceLabel: '$4,997/month',
    monthlyPrice: 4997,
    includedCredits: 10000,
    descriptionLines: [
      '10,000 Shared Credits',
      'First 10,000: Included ($0.50/credit equivalent)',
      '10,001-25,000: $0.10/credit',
      '25,001+: $0.05/credit',
      '5 Team seats',
      'Shared lists & collaboration tools',
      'Team analytics dashboard',
      'Advanced CRM features',
      'Custom integrations',
      'Dedicated account manager',
      'Priority support',
      'Automated team task assignment',
    ],
  },
];

export function PaymentCheckout() {
  const [purchaseType, setPurchaseType] = useState<'plan' | 'credits'>('plan');
  const [planId, setPlanId] = useState(PLAN_OPTIONS[0].id);
  const [individualCredits, setIndividualCredits] = useState(500);
  const [creditOptionId, setCreditOptionId] = useState(CREDIT_OPTIONS[0].id);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');

  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [human, setHuman] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const currentCredits = useAppStore((state) => state.credits);

  useEffect(() => {
    initFacebookPixel();
    trackFacebookPageView();
  }, []);

  const plan = useMemo(() => PLAN_OPTIONS.find((p) => p.id === planId) || PLAN_OPTIONS[0], [planId]);
  const creditOption = useMemo(
    () => CREDIT_OPTIONS.find((o) => o.id === creditOptionId) || CREDIT_OPTIONS[0],
    [creditOptionId]
  );

  const planCredits = plan.id === 'individual' ? individualCredits : plan.includedCredits;
  const planAmount =
    plan.id === 'individual'
      ? Math.max(300, Math.min(1300, plan.monthlyPrice + planCredits * 0.5))
      : plan.monthlyPrice;

  async function handleCheckout(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!human) {
      setError('Please confirm you are human.');
      return;
    }

    if (!fullName.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }

    setLoading(true);
    try {
      const payload =
        purchaseType === 'plan'
          ? {
              email: email.trim(),
              fullName: fullName.trim(),
              phone: phone.trim() || undefined,
              amount: planAmount,
              currency: 'USD',
              productName: plan.name,
              credits: planCredits,
              card: {
                number: cardNumber.replace(/\s+/g, ''),
                expMonth: expMonth.padStart(2, '0'),
                expYear: expYear.length === 2 ? `20${expYear}` : expYear,
                cvc,
                name: fullName.trim(),
                address: {
                  postalCode: postalCode.trim() || undefined,
                },
              },
              deviceInfo: {
                deviceType: /mobile|android|iphone|ipad/i.test(navigator.userAgent) ? 'MOBILE' : 'DESKTOP',
                browser: navigator.userAgent,
                macAddress: '00-00-00-00-00-00',
              },
              address: {
                line1: addressLine1.trim() || undefined,
                city: city.trim() || undefined,
                state: state.trim() || undefined,
                postalCode: postalCode.trim() || undefined,
                country: country.trim() || undefined,
              },
              honeypot,
              sendReceipt: true,
            }
          : {
              email: email.trim(),
              fullName: fullName.trim(),
              phone: phone.trim() || undefined,
              amount: creditOption.amount,
              currency: 'USD',
              productName: `Credits - ${creditOption.credits.toLocaleString()}`,
              credits: creditOption.credits,
              card: {
                number: cardNumber.replace(/\s+/g, ''),
                expMonth: expMonth.padStart(2, '0'),
                expYear: expYear.length === 2 ? `20${expYear}` : expYear,
                cvc,
                name: fullName.trim(),
                address: {
                  postalCode: postalCode.trim() || undefined,
                },
              },
              deviceInfo: {
                deviceType: /mobile|android|iphone|ipad/i.test(navigator.userAgent) ? 'MOBILE' : 'DESKTOP',
                browser: navigator.userAgent,
                macAddress: '00-00-00-00-00-00',
              },
              address: {
                line1: addressLine1.trim() || undefined,
                city: city.trim() || undefined,
                state: state.trim() || undefined,
                postalCode: postalCode.trim() || undefined,
                country: country.trim() || undefined,
              },
              honeypot,
              sendReceipt: true,
            };

      const result = await fetchAPI<any>(`${API_CONFIG.USERS_API}payments/checkout`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!result?.success) {
        setError(result?.error || 'Payment failed.');
        return;
      }
      setSuccess('Payment approved. Redirecting…');
      window.location.href = '/payment-confirmation';
    } catch (err: any) {
      setError(err?.message || 'Payment failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="checkout-page min-h-screen px-6 py-10 text-white bg-dark-bg">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-col gap-3">
          <p className="text-xs uppercase tracking-[0.3em] text-blue-400/80">Secure Checkout</p>
          <h1 className="text-3xl md:text-4xl font-semibold">Purchase Credits</h1>
          <p className="text-gray-300 text-sm md:text-base max-w-2xl">
            Enterprise‑grade security. Card details are transmitted securely and tokenized by QuickBooks; we never store them.
          </p>
          <div className="grid md:grid-cols-3 gap-3 mt-2">
            <div className="rounded-lg border border-blue-500/30 bg-blue-900/10 px-4 py-3 text-sm text-blue-100">
              Encrypted card vault + PCI‑grade handling
            </div>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-900/10 px-4 py-3 text-sm text-emerald-100">
              Real‑time fraud checks & tokenized payments
            </div>
            <div className="rounded-lg border border-purple-500/30 bg-purple-900/10 px-4 py-3 text-sm text-purple-100">
              Instant receipt + invoice record in QuickBooks
            </div>
          </div>
          {Number.isFinite(currentCredits) && currentCredits > 0 && (
            <p className="text-gray-300 text-sm mt-1">
              Current credits: <span className="text-white font-semibold">{currentCredits}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleCheckout} className="grid gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setPurchaseType('plan')}
              className={`rounded-lg px-4 py-3 text-white font-semibold ${
                purchaseType === 'plan' ? 'bg-blue-600' : 'bg-gray-800'
              }`}
            >
              Monthly Plans
            </button>
            <button
              type="button"
              onClick={() => setPurchaseType('credits')}
              className={`rounded-lg px-4 py-3 text-white font-semibold ${
                purchaseType === 'credits' ? 'bg-blue-600' : 'bg-gray-800'
              }`}
            >
              Add Credits
            </button>
          </div>

          {purchaseType === 'plan' && (
            <div className="grid md:grid-cols-3 gap-4">
              {PLAN_OPTIONS.map((p) => (
                <label
                  key={p.id}
                  className={`border rounded-2xl p-5 cursor-pointer transition-all ${
                    planId === p.id
                      ? 'border-blue-500 bg-gradient-to-b from-blue-900/30 to-blue-900/10 shadow-lg shadow-blue-500/10'
                      : 'border-gray-800 bg-gradient-to-b from-gray-900/60 to-gray-900/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-blue-400 font-semibold uppercase">{p.tier}</div>
                    {p.id === 'solopreneur' && (
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded bg-blue-600/30 text-blue-200">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="text-white text-xl font-semibold mt-2">
                    {p.id === 'individual' ? 'Starter' : p.id === 'solopreneur' ? 'Pro' : 'Team'}
                  </div>
                  <div className="text-gray-300 text-3xl font-semibold mt-2">
                    {p.id === 'individual' ? '$50' : p.id === 'solopreneur' ? '$2,997' : '$4,997'}
                    <span className="text-sm text-gray-400">/month</span>
                  </div>
                  <div className="text-gray-400 text-sm mt-2">{p.priceLabel}</div>
                  <ul className="text-gray-300 text-sm mt-3 space-y-2">
                    {p.descriptionLines.map((line) => (
                      <li key={line} className="flex gap-2">
                        <span className="text-cyan-400">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setPlanId(p.id)}
                      className={`w-full rounded-lg px-4 py-2 text-sm font-semibold ${
                        planId === p.id ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'
                      }`}
                    >
                      Get Started
                    </button>
                  </div>
                </label>
              ))}
            </div>
          )}

          {purchaseType === 'plan' && plan.id === 'individual' && (
            <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/40">
              <label className="block text-sm text-gray-300 mb-2">
                Credits per month (500–2,500)
              </label>
              <input
                type="number"
                min={500}
                max={2500}
                step={100}
                value={individualCredits}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const clamped = Math.max(500, Math.min(2500, Number.isFinite(value) ? value : 500));
                  setIndividualCredits(clamped);
                }}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
              />
              <div className="text-gray-300 text-sm mt-2">
                Monthly total: <span className="text-white font-semibold">${planAmount.toLocaleString()}</span>
              </div>
            </div>
          )}

          {purchaseType === 'plan' && plan.id !== 'individual' && (
            <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/40 text-gray-300 text-sm">
              Included credits: <span className="text-white font-semibold">{planCredits.toLocaleString()}</span>
            </div>
          )}

          {purchaseType === 'credits' && (
            <div className="grid md:grid-cols-3 gap-4">
              {CREDIT_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className={`border rounded-xl p-4 cursor-pointer ${
                    creditOptionId === opt.id
                      ? 'border-emerald-500 bg-emerald-900/20'
                      : 'border-gray-800 bg-gray-900/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="credits"
                    value={opt.id}
                    checked={creditOptionId === opt.id}
                    onChange={() => setCreditOptionId(opt.id)}
                    className="mr-2"
                  />
                  <div className="text-white font-semibold">{opt.label}</div>
                  <div className="text-gray-400 text-sm">{opt.priceLabel}</div>
                  <div className="text-gray-200 text-sm mt-2">${opt.amount.toLocaleString()} total</div>
                </label>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <label className="grid gap-2 text-sm text-gray-300">
              Full Name
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Email
              <input
                type="email"
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Phone (optional)
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Address Line 1
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              City
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              State
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Postal Code
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Country
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="grid gap-2 text-sm text-gray-300">
              Card Number
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="4111 1111 1111 1111"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Expiration (MM)
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={expMonth}
                onChange={(e) => setExpMonth(e.target.value)}
                placeholder="MM"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Expiration (YYYY)
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={expYear}
                onChange={(e) => setExpYear(e.target.value)}
                placeholder="YYYY"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              CVC
              <input
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={cvc}
                onChange={(e) => setCvc(e.target.value)}
                placeholder="123"
                required
              />
            </label>
          </div>

          <div className="hidden">
            <label>
              Company
              <input value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={human} onChange={(e) => setHuman(e.target.checked)} />
            I am human
          </label>

          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-900/40 border border-green-700 rounded px-3 py-2 text-sm text-green-200">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60"
          >
            {loading
              ? 'Processing…'
              : purchaseType === 'plan'
                ? `Pay $${planAmount.toLocaleString()}`
                : `Pay $${creditOption.amount.toLocaleString()}`}
          </button>
        </form>
      </div>
    </div>
  );
}
